import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import {
  AdminTestClientProviderInner,
  useAdminTestClient,
} from '@duke-hq/app-admin-web/lib/adminTestClient';
import { DataTable } from '@duke-hq/ui/components/DataTable/index';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { ControlledSelect } from '@duke-hq/ui/components/Form/Select';
import {
  Select as SearchableSelect,
  chakraComponents,
  type MultiValue,
  type OptionProps,
} from 'chakra-react-select';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import type { InputField } from '@duke-hq/tools-workflow';
import { sortSeedRows } from './sortSeedRows';
import {
  DataTableCellAction,
  DataTableSchema,
  type SortColumnInputSchema,
} from '@duke-hq/ui/components/DataTable/schema';
import { SortOrder } from '@duke-hq/lib/pagination';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { withPermission } from '../../withPermission';
import { SeedRuns } from './SeedRuns';
import { SeedTerminal } from './SeedTerminal';
import { testIds } from './testIds';

function ProviderOption<O extends { label: string; hasEmailDomain: boolean }>(
  props: OptionProps<O>,
) {
  return (
    <chakraComponents.Option {...props}>
      {props.data.label}
      {props.data.hasEmailDomain ? null : (
        <Text as="span" fontStyle="italic" color="fg.muted">
          {' — no email domain'}
        </Text>
      )}
    </chakraComponents.Option>
  );
}

export function SeedForm({
  strategy,
  fields,
  onSubmit,
  isRunning,
  alreadyRunning,
  onRunAnyway,
}: {
  strategy: string;
  fields: InputField[];
  onSubmit: (data: Record<string, string>) => void | Promise<void>;
  isRunning?: boolean;
  /** A previous run is still marked RUNNING and blocked this submit. */
  alreadyRunning?: boolean;
  onRunAnyway?: () => void;
}) {
  const { register, handleSubmit, control, setValue } = useForm<Record<string, string>>({
    defaultValues: Object.fromEntries(fields.map((f) => [f.name, f.defaultValue ?? ''])),
  });

  const hasProviderSelect = fields.some((f) => f.field === 'providerSelect');
  const { data: providers } = trpcAdmin.providers.all.useQuery({}, { enabled: hasProviderSelect });

  const providerOptions = useMemo(() => {
    if (typeof providers === 'undefined') {
      return [];
    }
    return [...providers]
      .sort((a, b) => a.id - b.id)
      .map((provider) => ({
        value: provider.id.toString(),
        label: `${provider.id}. ${provider.name}`,
        hasEmailDomain: provider.defaultEmailDomain != null,
      }));
  }, [providers]);

  const providerField = fields.find((f) => f.field === 'providerSelect');
  const courseSelectFields = useMemo(
    () => fields.filter((f) => f.field === 'courseSelect'),
    [fields],
  );
  const formValues = useWatch({ control });
  const selectedProviderId =
    providerField !== undefined ? formValues[providerField.name] : undefined;
  const tenantId = selectedProviderId ? Number(selectedProviderId) : Number.NaN;
  const hasValidProvider = Number.isInteger(tenantId) && tenantId > 0;

  const { data: courses } = trpcAdmin.providers.coursesForProvider.useQuery(
    { tenant_id: tenantId },
    { enabled: courseSelectFields.length > 0 && hasValidProvider },
  );

  const courseOptions = useMemo(
    () => (courses ?? []).map((course) => ({ value: course.id, label: course.title })),
    [courses],
  );

  useEffect(() => {
    courseSelectFields.forEach((field) => setValue(field.name, ''));
  }, [selectedProviderId, courseSelectFields, setValue]);

  const hasCourseMultiSelect = fields.some((f) => f.field === 'courseMultiSelect');
  const { data: publishedCourses } = trpcAdmin.courses.listCourses.useQuery(
    { filters: { status: 'PUBLISHED' } },
    { enabled: hasCourseMultiSelect },
  );
  const publishedCourseOptions = useMemo(
    () =>
      [...(publishedCourses ?? [])]
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((course) => ({ value: course.id, label: course.title })),
    [publishedCourses],
  );

  const renderField = (field: InputField) => {
    const name = field.name;

    if (field.field === 'providerSelect') {
      return (
        <ControlledSelect
          control={control}
          name={name}
          label={field.label}
          isRequired={field.required}
          rules={{ required: field.required }}
          options={providerOptions}
          components={{ Option: ProviderOption }}
        />
      );
    }

    if (field.field === 'courseSelect') {
      return (
        <ControlledSelect
          control={control}
          name={name}
          label={field.label}
          isRequired={field.required}
          rules={{ required: field.required }}
          options={courseOptions}
          isDisabled={!hasValidProvider}
        />
      );
    }

    if (field.field === 'courseMultiSelect') {
      const assignAllField = fields.find(
        (f) => f.kind === 'boolean' && f.name.startsWith('assignAll'),
      );
      const assignAll = assignAllField !== undefined && formValues[assignAllField.name] === 'true';
      return (
        <Controller
          control={control}
          name={name}
          render={({ field: multiField }) => {
            const selectedValues = multiField.value ? multiField.value.split(',') : [];
            return (
              <FormControl>
                <FormLabel>{field.label}</FormLabel>
                <SearchableSelect
                  isMulti
                  isDisabled={assignAll}
                  options={publishedCourseOptions}
                  value={publishedCourseOptions.filter((option) =>
                    selectedValues.includes(option.value),
                  )}
                  onChange={(options: MultiValue<{ value: string; label: string }>) =>
                    multiField.onChange(options.map((option) => option.value).join(','))
                  }
                />
              </FormControl>
            );
          }}
        />
      );
    }

    if (field.kind === 'select') {
      return (
        <ControlledSelect
          control={control}
          name={name}
          label={field.label}
          isRequired={field.required}
          rules={{ required: field.required }}
          options={field.options ?? []}
        />
      );
    }

    if (field.kind === 'number') {
      return (
        <Controller
          control={control}
          name={name}
          rules={{ required: field.required }}
          render={({ field: numberField }) => (
            <FormControl isRequired={field.required}>
              <FormLabel htmlFor={name}>{field.label}</FormLabel>
              <NumberInput
                min={field.min}
                max={field.max}
                value={numberField.value}
                onChange={(valueString) => numberField.onChange(valueString)}
              >
                <NumberInputField id={name} />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          )}
        />
      );
    }

    if (field.kind === 'boolean') {
      return (
        <Controller
          control={control}
          name={name}
          render={({ field: booleanField }) => (
            <FormControl>
              <Checkbox
                isChecked={booleanField.value === 'true'}
                onChange={(event) => booleanField.onChange(event.target.checked ? 'true' : 'false')}
              >
                {field.label}
              </Checkbox>
            </FormControl>
          )}
        />
      );
    }

    return (
      <FormControl isRequired={field.required}>
        <FormLabel htmlFor={name}>{field.label}</FormLabel>
        <Input id={name} {...register(name, { required: field.required })} />
      </FormControl>
    );
  };

  const fieldRows: InputField[][] = [];
  for (const field of fields) {
    const lastRow = fieldRows[fieldRows.length - 1];
    const lastRowId = lastRow?.[0]?.row;
    if (field.row !== undefined && lastRow && lastRowId === field.row) {
      lastRow.push(field);
    } else {
      fieldRows.push([field]);
    }
  }

  return (
    <Stack
      as="form"
      data-testid={testIds.form}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      h="full"
      gap={0}
    >
      <Stack gap={4} p={4} flex="1" overflowY="auto">
        <Text fontFamily="monospace" fontWeight="bold" fontSize="lg">
          {strategy}
        </Text>
        {fieldRows.map((row) => (
          <HStack
            key={row.map((field) => field.name).join('-')}
            align="flex-start"
            gap={4}
            width="100%"
          >
            {row.map((field) => (
              <Box key={field.name} width={field.width ?? '100%'}>
                {renderField(field)}
              </Box>
            ))}
          </HStack>
        ))}
      </Stack>
      <Box flexShrink={0} p={4}>
        {alreadyRunning ? (
          <Stack gap={2} align="flex-start">
            <Text fontSize="sm" color="fg.muted">
              A previous run of this seed is still marked as running. If the test-server restarted
              mid-run, that row will never close on its own.
            </Text>
            <Button colorScheme="orange" variant="outline" onClick={onRunAnyway} px={8}>
              Run anyway
            </Button>
          </Stack>
        ) : (
          <Button
            type="submit"
            colorScheme="purple"
            data-testid={testIds.submitButton}
            isLoading={isRunning}
            loadingText="Running…"
            px={8}
          >
            Run seed
          </Button>
        )}
      </Box>
    </Stack>
  );
}

function SeedsTable() {
  const [runStrategy, setRunStrategy] = useState<
    { name: string; fields: InputField[] } | undefined
  >(undefined);
  const [activeRun, setActiveRun] = useState<
    { strategy: string; values: Record<string, string>; force?: boolean } | undefined
  >(undefined);
  const [runChunks, setRunChunks] = useState<string[]>([]);
  const [runResult, setRunResult] = useState<'SUCCESS' | 'FAILURE' | 'ALREADY_RUNNING' | undefined>(
    undefined,
  );
  // Identifies one terminal's worth of output. Bumped whenever the modal is
  // opened for a (possibly different) strategy and whenever a run starts, so
  // SeedTerminal always remounts with a clean xterm instance rather than
  // inheriting whatever a previous run already wrote to it.
  const [runKey, setRunKey] = useState(0);
  const [sortOrder, setSortOrder] = useState<SortColumnInputSchema>({});
  const [searchQuery, setSearchQuery] = useState('');
  const toast = useToast();

  const { selectedStrategyName } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const onCloseDrawer = useCallback(() => {
    navigate(`/developer-tools/seeds/?${searchParams.toString()}`);
  }, [navigate, searchParams]);

  const testClient = useAdminTestClient();

  const { data: seedsData, refetch } = useQuery({
    queryKey: ['adminTestServer', 'seeds', 'list'],
    queryFn: () => testClient.seeds.list.query(),
  });

  const isRunning = typeof activeRun !== 'undefined' && typeof runResult === 'undefined';

  useEffect(() => {
    if (typeof activeRun === 'undefined') {
      return;
    }

    const subscription = testClient.seeds.runStream.subscribe(activeRun, {
      onData: (event) => {
        if (event.type === 'chunk') {
          setRunChunks((prev) => [...prev, event.data]);
          return;
        }

        if (event.type === 'already-running') {
          setRunChunks((prev) => [
            ...prev,
            `\r\n\x1b[33m[warn] A previous run is still marked as running.\x1b[0m\r\n`,
          ]);
          setRunResult('ALREADY_RUNNING');
          return;
        }

        setRunResult(event.status);
        void refetch();
        toast({
          title: event.status === 'SUCCESS' ? 'Success' : 'Error',
          description:
            event.status === 'SUCCESS'
              ? `${activeRun.strategy} completed successfully`
              : `${activeRun.strategy} failed`,
          status: event.status === 'SUCCESS' ? 'success' : 'error',
          duration: event.status === 'SUCCESS' ? 5000 : 10000,
          isClosable: true,
        });
      },
      onError: (error) => {
        setRunResult('FAILURE');
        setRunChunks((prev) => [...prev, `\r\n\x1b[31m[error] ${error.message}\x1b[0m\r\n`]);
        void refetch();
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [activeRun, testClient, refetch, toast]);

  const startRun = useCallback(
    (strategy: string, values: Record<string, string>, force?: boolean) => {
      setRunChunks([]);
      setRunResult(undefined);
      setActiveRun({ strategy, values, force });
      setRunKey((key) => key + 1);
    },
    [],
  );

  const onSortChange = useCallback((columnName: string, direction: SortOrder | undefined) => {
    setSortOrder(direction === undefined ? {} : { [columnName]: direction });
  }, []);

  const handleAction = useCallback(
    async (action: DataTableCellAction<{ name: string; fields: InputField[] }>) => {
      switch (action.kind) {
        case 'run.seed': {
          setRunStrategy(action.data);
          setActiveRun(undefined);
          setRunChunks([]);
          setRunResult(undefined);
          setRunKey((key) => key + 1);
          break;
        }
        case 'view.runs': {
          navigate(`/developer-tools/seeds/${action.data.name}?${searchParams.toString()}`);
          break;
        }
      }
    },
    [navigate, searchParams],
  );

  const filteredStrategies = useMemo(() => {
    if (typeof seedsData === 'undefined') {
      return undefined;
    }
    const search = searchQuery.trim().toLowerCase();
    const matched = search
      ? seedsData.strategies.filter((strategy) => strategy.name.toLowerCase().includes(search))
      : seedsData.strategies;
    return sortSeedRows(matched, sortOrder);
  }, [seedsData, searchQuery, sortOrder]);

  const data = useMemo(() => {
    if (typeof filteredStrategies === 'undefined') {
      return undefined;
    }

    return {
      headers: {
        name: { label: 'Name', sortable: true, currentSortOrder: sortOrder.name },
        status: { label: 'Status', sortable: true, currentSortOrder: sortOrder.status },
        lastRunAt: { label: 'Last run', sortable: true, currentSortOrder: sortOrder.lastRunAt },
        run: { label: 'Run', sortable: false },
        view: { label: 'View', sortable: false },
      },
      rows: filteredStrategies.map((strategy) => ({
        id: strategy.name,
        cells: {
          name: { type: 'raw', value: strategy.name },
          status: {
            type: 'status',
            variant: 'subtle',
            label: strategy.inProgress ? 'RUNNING' : strategy.status,
            colorScheme:
              strategy.status === 'SUCCESS'
                ? 'green'
                : strategy.status === 'FAILURE'
                  ? 'red'
                  : strategy.inProgress
                    ? 'purple'
                    : 'gray',
          },
          lastRunAt:
            strategy.lastRunAt !== null
              ? {
                  type: 'date',
                  value: strategy.lastRunAt,
                  label: strategy.lastRunAt.toLocaleString('en-AU'),
                }
              : undefined,
          run: {
            type: 'action',
            label: 'Run…',
            kind: 'run.seed',
            data: { name: strategy.name, fields: strategy.fields },
            size: 'sm',
            variant: 'outline',
            colorScheme: 'orange',
          },
          view: {
            type: 'action',
            label: 'View',
            kind: 'view.runs',
            data: { name: strategy.name, fields: strategy.fields },
            size: 'sm',
            variant: 'outline',
            colorScheme: 'gray',
          },
        },
      })),
    } satisfies DataTableSchema;
  }, [filteredStrategies, sortOrder]);

  if (typeof seedsData !== 'undefined' && (!seedsData.available || seedsData.unreachable)) {
    return (
      <Alert status="warning" data-testid={testIds.unavailableBanner}>
        <AlertIcon />
        <AlertDescription>
          {seedsData.unreachable ? (
            <>
              Seed execution service is unreachable. Start the test-servers with{' '}
              <code>yarn dev:all</code>.
            </>
          ) : (
            <>
              Seed tools run in local development only. Start test-servers with{' '}
              <code>yarn dev:all</code>.
            </>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  if (typeof seedsData === 'undefined' || typeof data === 'undefined') {
    return <div>Loading...</div>;
  }

  return (
    <>
      <TableViewLayout
        pageHeader={[
          <PageHeading key="seeds" data-testid={testIds.heading}>
            <Trans>Seed Data</Trans>
          </PageHeading>,
        ]}
        filters={
          <SearchInput maxW="search-input" onCommit={setSearchQuery} placeholder="Search seeds" />
        }
        tableActionsLeft={null}
        table={
          <DataTable
            data={data}
            onSortChange={onSortChange}
            handleAction={handleAction}
            tableProps={{
              colorScheme: 'purple',
              size: 'sm',
            }}
          />
        }
        pagination={null}
      />
      <SeedRuns
        strategyName={selectedStrategyName}
        isOpen={typeof selectedStrategyName === 'string'}
        onClose={onCloseDrawer}
      />
      <FormModalWrapper
        size="wide"
        modalContentHeight="90vh"
        modalCloseButton={!isRunning}
        isOpen={typeof runStrategy !== 'undefined'}
        onClose={() => {
          if (!isRunning) {
            setRunStrategy(undefined);
            setActiveRun(undefined);
            setRunResult(undefined);
            setRunChunks([]);
          }
        }}
        modalContent={
          typeof runStrategy !== 'undefined' ? (
            <HStack align="stretch" gap={4} h="full" w="full" overflow="hidden">
              <Box flex="1" minW="420px" h="full">
                <SeedForm
                  strategy={runStrategy.name}
                  fields={runStrategy.fields}
                  isRunning={isRunning}
                  alreadyRunning={runResult === 'ALREADY_RUNNING'}
                  onRunAnyway={() => {
                    if (typeof activeRun !== 'undefined') {
                      startRun(activeRun.strategy, activeRun.values, true);
                    }
                  }}
                  onSubmit={(formData) => startRun(runStrategy.name, formData)}
                />
              </Box>
              <Box flexShrink={0} h="full" pt={4} pr={4} pb={4}>
                <SeedTerminal key={runKey} chunks={runChunks} />
              </Box>
            </HStack>
          ) : null
        }
      />
    </>
  );
}

export const SeedsPage = withPermission('canAccessDeveloperTools', () => {
  return (
    <AdminTestClientProviderInner>
      <SeedsTable />
    </AdminTestClientProviderInner>
  );
});
